import { useState, useEffect, useCallback } from 'react';
import { bffQuery } from '@parsina/framework/clinical/src/lib/bffClient';

const MY_CODES = `
  query MyGiftCodes {
    myGiftCodes { id code parseh smartVisits status expiresAtMs redeemedAtMs }
  }
`;

const REDEEM = `
  mutation RedeemGiftCode($code: String!) {
    redeemGiftCode(code: $code) {
      granted
      giftCode { id code parseh smartVisits status redeemedAtMs }
      balance { balance currency }
    }
  }
`;

/**
 * The doctor's gift codes, and the one action that spends them.
 *
 * `redeem` resolves with the result rather than throwing on a rejected code:
 * a wrong code is an ordinary outcome of this form, not an exception, and the
 * card renders it as a field message. Only the shape of the result says which.
 */
export function useGiftCodes(doctorId) {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    if (!doctorId) return;
    setLoading(true);
    bffQuery(MY_CODES)
      .then((data) => setCodes(data?.myGiftCodes || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [doctorId]);

  useEffect(() => { refresh(); }, [refresh]);

  const redeem = useCallback(async (code) => {
    const trimmed = (code || '').trim();
    if (!trimmed) return { ok: false, code: 'EMPTY' };
    try {
      const data = await bffQuery(REDEEM, { code: trimmed });
      const result = data?.redeemGiftCode;
      if (!result) return { ok: false, code: 'UNKNOWN' };
      refresh();
      return {
        ok: true,
        granted: result.granted,
        smartVisits: result.giftCode?.smartVisits ?? 0,
        balance: result.balance?.balance ?? null,
      };
    } catch (err) {
      // The BFF's message is written for the doctor — «قبلاً استفاده شده»,
      // «منقضی شده» — and each is a different instruction to them, so it is
      // shown rather than replaced with one generic line.
      const gqlError = err?.graphQLErrors?.[0];
      return {
        ok: false,
        code: gqlError?.extensions?.code || 'UNKNOWN',
        // The BFF's own message is written for people; a transport error is not.
        message: gqlError?.message || '',
      };
    }
  }, [refresh]);

  return { codes, loading, redeem, refresh };
}
